import { NgClass, NgStyle } from '@angular/common';
import { Component, Input, OnDestroy, OnInit, inject } from '@angular/core';
import { Router } from '@angular/router';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { takeUntil } from 'rxjs/operators';
import { HttpErrorCodes } from 'src/app/core/constants/error.constants';
import { FormFieldName, FormType } from 'src/app/core/constants/form.constants';
import { UserEventAction } from 'src/app/core/constants/user-event.constants';
import { EmailResetHelper } from 'src/app/core/helpers/email-reset.helper';
import { IColor, IText } from 'src/app/core/models/content.model';
import { FormRegex, FormValidatorType } from 'src/app/core/models/form-validator.model';
import { IRadioControl, ITextControl, RadioDisplayType } from 'src/app/core/models/form.model';
import { IHttpError } from 'src/app/core/models/http.model';
import { ReCaptchaAction } from 'src/app/core/models/reCaptcha.request';
import { MaskedUserContactDetails } from 'src/app/core/models/user.model';
import { SiteService } from 'src/app/core/services/site.service';
import { UserService } from 'src/app/core/services/user.service';
import { BaseFormComponent } from 'src/app/features/form/base-form.component';
import { ModalOptions } from 'src/app/features/modal/modal.model';
import { ModalService } from 'src/app/features/modal/modal.service';

import { FormComponent } from '../../../features/form/form.component';
import { ButtonLoaderDirective } from '../../directives/button-loader.directive';
import { InternalInnerHtmlDirective } from '../../directives/internal-inner-html.directive';
import {
  IPasswordResetModalInputs,
  PasswordResetTokenByCardNumberRequest,
  UserPasswordResetTokenCreateRequest
} from './password-reset-modal.model';

@Component({
  selector: 'air-password-reset-modal',
  templateUrl: './password-reset-modal.component.html',
  imports: [InternalInnerHtmlDirective, NgStyle, FormComponent, NgClass, ButtonLoaderDirective, TranslatePipe]
})
export class PasswordResetModalComponent extends BaseFormComponent<UserPasswordResetTokenCreateRequest> implements OnInit, OnDestroy {
  private modalService = inject(ModalService);
  private translateService = inject(TranslateService);
  private userService = inject(UserService);
  private siteService = inject(SiteService);
  private emailResetHelper = inject(EmailResetHelper);
  private router = inject(Router);

  @Input() inputs?: IPasswordResetModalInputs;
  @Input() modalOptions: ModalOptions;
  @Input() onCloseCallback?: () => void;
  @Input() onSuccessCloseCallback?: () => void;
  @Input() standalone;
  @Input() color?: IColor;

  tabControlName = 'methodType';
  selectedView: string;
  showResponse = false;
  showEmailReset = false;
  isSuccess = false;

  passwordResetText?: IText;
  passwordResetErrorNoAccount?: IText;
  passwordResetErrorBlockedAccount?: IText;
  passwordResetErrorInactiveCard?: IText;
  passwordResetErrorNoEmail?: IText;
  passwordResetNoEmailAccess?: IText;
  passwordResetSuccess?: IText;
  passwordResetMessage?: IText;
  passwordResetBaseUrl?: string;

  emailResetPageUrl?: string;
  emailResetErrorNoPhone?: IText;
  emailResetErrorNoBirthDate?: IText;
  emailResetText?: IText;
  emailResetCodeText?: IText;
  cardNumber?: string;
  maskedUserContactDetails?: MaskedUserContactDetails;

  alternateReCaptchaActions = [ReCaptchaAction.sendPasswordResetToken, ReCaptchaAction.sendPasswordResetTokenByCardNumber];
  preValues: UserPasswordResetTokenCreateRequest & PasswordResetTokenByCardNumberRequest;

  ngOnInit(): void {
    super.ngOnInit();
    let initTabView = FormFieldName.emailAddress;
    this.preValues = this.inputs?.preValues as UserPasswordResetTokenCreateRequest & PasswordResetTokenByCardNumberRequest;
    if (this.preValues) {
      if (this.preValues.cardNumber) {
        initTabView = FormFieldName.cardNumber;
      }
    }

    this.siteService.pageContext.pipe(takeUntil(this.destroy$)).subscribe((data) => {
      this.passwordResetBaseUrl = data?.passwordResetPageUrl;
      this.passwordResetText = data?.passwordToken;
      this.passwordResetErrorNoAccount = data?.passwordTokenNoAccount;
      this.passwordResetErrorBlockedAccount = data?.passwordTokenBlockedAccount;
      this.passwordResetErrorInactiveCard = data?.passwordTokenInactiveCard;
      this.passwordResetErrorNoEmail = data?.passwordTokenNoEmail;
      this.passwordResetNoEmailAccess = data?.passwordTokenNoEmailAccess;
      this.passwordResetSuccess = data?.passwordTokenSuccess;

      this.emailResetPageUrl = data?.emailResetPageUrl;
      this.emailResetErrorNoPhone = data?.emailResetNoPhone;
      this.emailResetErrorNoBirthDate = data?.emailResetNoBirthDate;
      this.emailResetText = data?.emailReset;
      this.emailResetCodeText = data?.emailResetCode;
    });

    this.form = [
      {
        controls: [
          {
            type: FormType.radio,
            name: this.tabControlName,
            value: initTabView,
            displayType: RadioDisplayType.tabs,
            options: [
              { value: FormFieldName.emailAddress, text: this.translateService.instant('account.label.email') as string },
              { value: FormFieldName.cardNumber, text: this.translateService.instant('account.label.card_number') as string }
            ],
            color: this.color
          } as IRadioControl,
          {
            type: FormType.email,
            name: FormFieldName.emailAddress,
            autocomplete: 'email',
            label: this.translateService.instant('account.label.email') as string,
            value: this.preValues && this.preValues.emailAddress ? this.preValues.emailAddress : null,
            showWhen: {
              controlName: this.tabControlName,
              value: FormFieldName.emailAddress
            },
            placeholder: this.translateService.instant('account.label.your_email') as string,
            validators: [
              {
                validator: FormValidatorType.required,
                errorMessage: this.translateService.instant('account.error.email.required') as string
              },
              {
                validator: FormValidatorType.email,
                errorMessage: this.translateService.instant('account.error.email.invalid') as string
              }
            ]
          } as ITextControl,
          {
            type: FormType.text,
            name: FormFieldName.cardNumber,
            label: this.translateService.instant('account.label.card_number') as string,
            value: this.preValues?.cardNumber,
            description: `<p>${this.translateService.instant('account.description.card_number_barcode') as string}</p>`,
            textOnlyDescription: true,
            showWhen: {
              controlName: this.tabControlName,
              value: FormFieldName.cardNumber
            },
            placeholder: this.translateService.instant('account.label.your_card_number') as string,
            validators: [
              {
                validator: FormValidatorType.required,
                errorMessage: this.translateService.instant('account.error.card_number.required') as string
              },
              {
                validator: FormValidatorType.pattern(FormRegex.cardNumber.getRegExp()),
                errorMessage: this.translateService.instant('account.error.card_number.invalid') as string
              }
            ]
          } as ITextControl
        ]
      }
    ];

    this.userEventSource = `${this.inputs?.userEventSource} - password reset`;
  }

  async submitValues(values: UserPasswordResetTokenCreateRequest | PasswordResetTokenByCardNumberRequest): Promise<void> {
    this.isSubmitting = true;
    this.showResponse = false;
    this.isSuccess = false;

    this.selectedView = values[this.tabControlName] as string;
    delete values[this.tabControlName];
    values.passwordResetBaseUrl = `${this.platformHelper.getCurrentAbsoluteBaseUrl()}${this.passwordResetBaseUrl}`;

    switch (this.selectedView) {
      case FormFieldName.emailAddress:
        delete values['cardnumber'];
        await this.sendPasswordResetToken(values as UserPasswordResetTokenCreateRequest).finally(() => (this.isSubmitting = false));
        break;
      case FormFieldName.cardNumber:
        delete values['emailAddress'];
        await this.sendPasswordResetTokenByCardNumber(values as PasswordResetTokenByCardNumberRequest).finally(
          () => (this.isSubmitting = false)
        );
        break;
    }

    this.pushUserSuccessEvent();

    this.isSuccess = true;
    this.showResponse = true;
  }

  async back(event: Event): Promise<void> {
    this.pushUserEvent(UserEventAction.link, event.text(), 'back');
    this.showEmailReset = false;
    this.modalOptions.onCloseCallback = this.onCloseCallback;
    if (this.inputs?.prevModal) {
      return this.modalService.openWithOnClose(this.modalOptions, this.inputs.prevModal, {
        [FormFieldName.cardNumber]: this.preValues?.cardNumber,
        onCloseCallback: this.modalOptions.onCloseCallback,
        onLoginCallback: this.inputs.prevLoginCallback,
        checkLoginCloseCallback: this.inputs.prevCheckLoginCloseCallback,
        completeProfilePage: this.inputs.prevCompleteProfilePage,
        alternateMode: this.inputs.alternateMode,
        userEventSource: this.inputs.userEventSource,
        loginUsernameNotice: this.inputs.prevLoginUsernameNotice,
        partnerNotice: this.inputs.prevPartnerNotice,
        color: this.inputs.prevColor
      });
    }
    this.closeModal();
  }

  openResetForm(event: Event): void {
    this.pushUserEvent(UserEventAction.link, event.text(), 'retry');
    this.showResponse = false;
    this.showEmailReset = false;

    setTimeout(() => {
      const view = this.selectedView ?? (this.preValues?.cardNumber ? FormFieldName.cardNumber : FormFieldName.emailAddress);
      this.formComponent?.form?.get(this.tabControlName)?.setValue(view);
    });
  }

  closeModal(pushUserEvent = false, event?: Event): void {
    if (pushUserEvent) {
      this.pushUserEvent(UserEventAction.close, event?.text());
    }

    this.modalService.close();
  }

  triggerEmailResetModal(event: Event): void {
    this.pushUserEvent(UserEventAction.link, event.text());
    this.showEmailReset = false;

    if (!this.maskedUserContactDetails?.maskedPhoneNumber) {
      this.passwordResetMessage = this.emailResetErrorNoPhone;
      this.showResponse = true;
      return;
    } else if (!this.maskedUserContactDetails.hasBirthDate) {
      this.passwordResetMessage = this.emailResetErrorNoBirthDate;
      this.showResponse = true;
      return;
    }

    if (!this.emailResetText || !this.emailResetCodeText || !this.inputs?.userEventSource) {
      return;
    }

    void this.emailResetHelper.triggerEmailResetModal({
      emailResetText: this.emailResetText,
      emailResetCodeText: this.emailResetCodeText,
      cardNumber: this.cardNumber!,
      maskedPhoneNumber: this.maskedUserContactDetails!.maskedPhoneNumber!,
      userEventSource: this.inputs?.userEventSource,
      verifyCallback: (phoneNumber: string, emailResetCode: string) => this.emailVerifyCallback(phoneNumber, emailResetCode),
      closeCallback: () => this.emailCloseCallback()
    });
  }

  private async emailVerifyCallback(phoneNumber: string, emailResetCode: string): Promise<void> {
    this.isSubmitting = false;

    this.router.navigateByUrl(this.emailResetPageUrl ?? '/', {
      state: {
        cardNumber: this.cardNumber,
        emailResetCode: emailResetCode,
        phoneNumber
      }
    });
  }

  private emailCloseCallback(): void {
    this.isSubmitting = false;
  }

  private async sendPasswordResetToken(values: UserPasswordResetTokenCreateRequest): Promise<void> {
    await this.reCaptchaService
      .verify(ReCaptchaAction.sendPasswordResetToken, values, this.userService.sendPasswordResetToken.bind(this.userService))
      .catch((error: IHttpError) => {
        switch (error?.error?.code) {
          case HttpErrorCodes.accountNotFound:
            this.passwordResetMessage = this.passwordResetErrorNoAccount;
            this.showResponse = true;
            break;
          case HttpErrorCodes.invalidAccount:
            this.passwordResetMessage = this.passwordResetErrorBlockedAccount;
            this.showResponse = true;
            break;
        }

        this.formComponent.reCaptchaFallbackComponent?.reset();
        throw error;
      });

    if (this.onSuccessCloseCallback) {
      this.modalOptions.onCloseCallback = this.onSuccessCloseCallback;
    }

    this.passwordResetMessage = Object.assign({}, this.passwordResetSuccess);
    if (this.passwordResetMessage) {
      this.passwordResetMessage.body = this.passwordResetMessage.body.format([values.emailAddress ?? '']);
    }
  }

  private async sendPasswordResetTokenByCardNumber(values: PasswordResetTokenByCardNumberRequest): Promise<void> {
    this.maskedUserContactDetails = await this.reCaptchaService
      .verify(
        ReCaptchaAction.sendPasswordResetTokenByCardNumber,
        values,
        this.userService.sendPasswordResetTokenByCardNumber.bind(this.userService)
      )
      .catch((error: IHttpError) => {
        switch (error?.error?.code) {
          case HttpErrorCodes.accountNotFound:
            this.passwordResetMessage = this.passwordResetErrorNoAccount;
            this.showResponse = true;
            break;
          case HttpErrorCodes.invalidAccount:
            this.passwordResetMessage = this.passwordResetErrorBlockedAccount;
            this.showResponse = true;
            break;
          case HttpErrorCodes.inactiveCard:
            this.passwordResetMessage = this.passwordResetErrorInactiveCard;
            this.showResponse = true;
            break;
          case HttpErrorCodes.emailAddressNotFound:
            this.passwordResetMessage = this.passwordResetErrorNoEmail;
            this.showResponse = true;
        }

        this.formComponent.reCaptchaFallbackComponent?.reset();
        throw error;
      });
    this.cardNumber = values.cardNumber;

    if (this.onSuccessCloseCallback) {
      this.modalOptions.onCloseCallback = this.onSuccessCloseCallback;
    }

    this.passwordResetMessage = Object.assign({}, this.passwordResetSuccess);
    if (this.passwordResetMessage) {
      this.passwordResetMessage.body = this.passwordResetMessage.body.format([this.maskedUserContactDetails.maskedEmailAddress]);
      this.showEmailReset = true;
    }
  }
}
